import DemoBadge from '@/components/feature/DemoBadge';
import StatusBadge from './StatusBadge';

const AI_PIPELINE = [
  { label: 'Discover', icon: 'ri-radar-line', automation: true },
  { label: 'Extract', icon: 'ri-file-text-line', automation: true },
  { label: 'Prepare draft', icon: 'ri-sparkling-line', automation: true },
  { label: 'Editorial review', icon: 'ri-user-star-line', automation: false },
  { label: 'Publish', icon: 'ri-send-plane-line', automation: false },
];

const AI_FEED = [
  { id: 'ai-1', icon: 'ri-radar-line', title: 'Story discovered', detail: 'A regional news feed surfaced a charity tournament result for the Inland Empire desk.', time: '9 min ago', tag: 'Discovery' },
  { id: 'ai-2', icon: 'ri-file-text-line', title: 'Content extracted', detail: 'Structured headline, summary and body pulled from a course press release.', time: '24 min ago', tag: 'Extraction' },
  { id: 'ai-3', icon: 'ri-sparkling-line', title: 'Draft prepared', detail: 'A sample draft was prepared and queued for editorial review — no model is connected in this preview.', time: '1 hr ago', tag: 'Preparation' },
  { id: 'ai-4', icon: 'ri-price-tag-3-line', title: 'Region tagged', detail: 'Suggested publication regions: Orange County, Los Angeles.', time: '2 hrs ago', tag: 'Tagging' },
  { id: 'ai-5', icon: 'ri-shield-check-line', title: 'Quality check', detail: 'Readability and duplicate checks flagged one paragraph for a human edit.', time: '3 hrs ago', tag: 'Compliance' },
];

const CAPABILITIES = [
  { icon: 'ri-radar-line', title: 'Event & story discovery', text: 'Scan approved public sources for local golf news worth covering.' },
  { icon: 'ri-file-text-line', title: 'Structured extraction', text: 'Turn raw documents and press releases into clean headline, summary and body fields.' },
  { icon: 'ri-sparkling-line', title: 'First-draft preparation', text: 'Produce a reviewable first draft — always for a human editor, never auto-published.' },
  { icon: 'ri-translate-2', title: 'Region tagging', text: 'Suggest which markets a story belongs to before it is scheduled.' },
];

export default function AiActivityPreview() {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">AI activity preview</h2>
        <DemoBadge label="Concept — no AI connected" />
      </div>

      <div className="flex items-start gap-3 bg-accent-50 border border-accent-300 border-l-4 border-l-accent-500 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-alert-line"></i></span>
        <p className="text-sm text-foreground-800">
          <strong className="font-semibold">Conceptual preview.</strong> No AI model, agent or background job is connected. The activity below illustrates how automated editorial support could look once built — everything shown is simulated sample data.
        </p>
      </div>

      {/* Pipeline */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
        <p className="font-heading font-bold text-lg text-foreground-950 mb-4">Conceptual editorial pipeline</p>
        <div className="flex flex-wrap items-center gap-2">
          {AI_PIPELINE.map((s, i) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-label uppercase tracking-wide text-[10px] font-semibold whitespace-nowrap ${s.automation ? 'bg-primary-100 border-primary-200 text-primary-700' : 'bg-background-100 border-background-200 text-foreground-600'}`}>
                <span className="w-4 h-4 flex items-center justify-center"><i className={s.icon}></i></span>
                {s.label}
                {!s.automation && <span className="text-[9px] opacity-70">· human</span>}
              </span>
              {i < AI_PIPELINE.length - 1 && <span className="w-4 flex items-center justify-center text-foreground-300"><i className="ri-arrow-right-line"></i></span>}
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-foreground-500">Automated steps are always followed by a required human editorial review — nothing is ever auto-published.</p>
      </div>

      {/* Capabilities */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {CAPABILITIES.map((c) => (
          <div key={c.title} className="bg-background-50 border border-background-200 rounded-lg p-4">
            <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl"><i className={c.icon}></i></span>
            <p className="mt-3 font-heading font-bold text-base text-foreground-950 leading-snug">{c.title}</p>
            <p className="mt-1.5 text-xs text-foreground-600 leading-relaxed">{c.text}</p>
          </div>
        ))}
      </div>

      {/* Feed */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-4 md:px-5 py-3.5 border-b border-background-200 flex items-center gap-2.5">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-foreground-950 text-accent-400"><i className="ri-history-line"></i></span>
          <p className="font-heading font-bold text-lg text-foreground-950">Simulated AI activity</p>
        </div>
        <ul className="divide-y divide-background-200">
          {AI_FEED.map((a) => (
            <li key={a.id} className="px-4 md:px-5 py-3.5 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 shrink-0"><i className={a.icon}></i></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950 truncate">{a.title}</p>
                <p className="text-xs text-foreground-500 line-clamp-2">{a.detail}</p>
              </div>
              <StatusBadge tone="neutral" className="hidden sm:inline-flex">{a.tag}</StatusBadge>
              <span className="font-label uppercase tracking-wide text-[10px] text-foreground-500 whitespace-nowrap hidden md:block">{a.time}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}